import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Fish } from "lucide-react";
import { supabase } from "./supabaseClient";
import { BOAT, SHORE, parseLbsOz, formatLbs } from "./cfsa-data";

function formatKgLbsOz(kg) {
  return formatLbs(kg * 2.20462);
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function calcPct(species, location, kg) {
  const data = location === "boat" ? BOAT : SHORE;
  const record = data.find((r) => r.species === species);
  if (!record) return null;
  const specimenLbs = parseLbsOz(record.sLbs);
  if (!specimenLbs) return null;
  return (kg * 2.20462 / specimenLbs) * 100;
}

export default function LatestCatches() {
  const [catches, setCatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fetchCatches = async () => {
      // First, get all catches from the last 3 months to check the count
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      const threeMonthsAgoStr = threeMonthsAgo.toISOString().split('T')[0];

      const { data: recentCatches, error } = await supabase
        .from("catches")
        .select("id, angler_name, location, species, weight_kg, weigh_date")
        .gte("weigh_date", threeMonthsAgoStr)
        .order("weigh_date", { ascending: false });

      if (error) {
        console.error("Error fetching catches:", error);
        setLoading(false);
        return;
      }

      let displayCatches;
      if (recentCatches.length < 6) {
        // If less than 6 catches in 3 months, show all from 3 months
        displayCatches = recentCatches;
      } else {
        // If 6 or more catches, limit to last 2 months
        const twoMonthsAgo = new Date();
        twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
        const twoMonthsAgoStr = twoMonthsAgo.toISOString().split('T')[0];

        displayCatches = recentCatches.filter(c => c.weigh_date >= twoMonthsAgoStr);
      }

      // Limit to 15 most recent
      setCatches(displayCatches.slice(0, 15));
      setLoading(false);
    };

    fetchCatches();
  }, []);

  if (loading || catches.length === 0) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative border-y border-cyan-400/10 bg-gradient-to-b from-cyan-950/40 via-slate-900 to-slate-950 px-4 py-8 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
              <Fish className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Members' Catches</p>
              <h2 className="text-3xl font-bold tracking-tight text-white">Latest Fish Caught</h2>
            </div>
          </div>
          <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 rounded-xl border border-cyan-300/40 bg-cyan-400/15 px-4 py-2 text-sm font-semibold text-cyan-300 transition hover:bg-cyan-400/25 shrink-0">
            {open ? "Hide" : "Show"}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-6 w-6 transition-transform duration-300 ${open ? "rotate-180" : ""}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
          </button>
        </div>

        {open && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {catches.map((c) => {
              const pct = calcPct(c.species, c.location, c.weight_kg);
              return (
                <div key={c.id} className="rounded-2xl border border-white/10 bg-white/5 p-5 panel-lift">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <span className="font-semibold text-white">{c.angler_name}</span>
                    <span className={`shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${c.location === "boat" ? "bg-sky-400/20 text-sky-300" : "bg-emerald-400/20 text-emerald-300"}`}>
                      {c.location}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300 mb-1">{c.species}</p>
                  <p className="text-lg font-bold text-white">{formatKgLbsOz(c.weight_kg)}</p>
                  {pct !== null && (
                    <>
                      <p className={`mt-1 text-sm font-semibold ${pct >= 100 ? "text-yellow-400" : "text-emerald-400"}`}>
                        {pct.toFixed(1)}% of CFSA specimen
                      </p>
                      <div className="mt-2 h-1.5 w-full rounded-full bg-white/10">
                        <div
                          className={`h-1.5 rounded-full transition-all ${pct >= 100 ? "bg-yellow-400" : "bg-emerald-400"}`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                    </>
                  )}
                  {c.weigh_date && <p className="mt-2 text-xs text-slate-500">{formatDate(c.weigh_date)}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.section>
  );
}
